import React, { FC, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box } from "@material-ui/core";
import { getClosestResizePixel, ResizePixel } from "./ResizeHelper";
import { useTranslation } from "Providers/TranslationProvider";
import { getColorRgbaFromHex } from "Helpers/ColorHelper";
import { color, px } from "Configs/theme";
import { isIpadOs } from "Helpers/PlatformHelper";
import { CalendarEntry } from "Components/Calendar/CalendarEntryHelper";
import moment from "moment";

export type ResizeDirection = "left" | "right";

// Minimum duration in minutes for any calendar entry
const MIN_DURATION_MINUTES = 5;

interface Props {
	columnWidth: number;
	calendarEntry: CalendarEntry;
	calendarEntryWidth: number;
	calendarEntryLeft: number;
	resizeDirection: ResizeDirection;
	resizePixels: ResizePixel[];
	isResizing: boolean;
	isTooltipVisible: boolean;
	isFullDay?: boolean;
	setResizeWidth: (px: number) => void;
	setResizeLeft: (px: number) => void;
	onChange: (dateTime: number) => void;
	selectScheduleEntry: () => void;
	setIsResizing: (isResizing: boolean) => void;
}

const ResizeView: FC<Props> = (props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const resizePixelRef = useRef<ResizePixel>();
	const isClickingRef = useRef(false);
	const isIpadOsRef = useRef(isIpadOs());
	const startXRef = useRef(0);
	const initialWidthRef = useRef(0);
	const initialLeftRef = useRef(0);

	function onResizeStart(event: React.MouseEvent | React.TouchEvent) {
		isClickingRef.current = true;
		props.setIsResizing(true);

		startXRef.current = 'touches' in event ? event.targetTouches[0].clientX : event.clientX;
		initialWidthRef.current = props.calendarEntryWidth;
		initialLeftRef.current = props.calendarEntryLeft;

		document.addEventListener("mouseup", onResizeEnd);
		document.addEventListener("mousemove", onResizeMouseMove);
		if (isIpadOsRef.current) {
			document.addEventListener("touchend", onResizeEnd);
			document.addEventListener("touchmove", onResizeTouchMove);
		}
	}

	function onResizeMouseMove(event: MouseEvent) {
		onResizeMove(event.pageX);
	}

	function onResizeTouchMove(event: TouchEvent) {
		if (event.targetTouches.length) {
			onResizeMove(event.targetTouches[0].pageX);
		}
	}

	function onResizeMove(currentX: number) {
		isClickingRef.current = false;

		const deltaX = currentX - startXRef.current;

		const nextResizePixel = getClosestResizePixel(props.resizePixels, currentX);
		if (nextResizePixel) {
			// Calculate the potential new start/end time
			const newDateTime = nextResizePixel.dateTime.unix();
			
			// Apply constraint validation during resize movement
			if (props.resizeDirection === "left") {
				// Calculate what the duration would be with the new start time
				const endTime = props.calendarEntry.endTime;
				const newDurationSeconds = endTime - newDateTime;
				const newDurationMinutes = newDurationSeconds / 60;
				
				// Only block if it would make the entry too short
				if (newDurationMinutes < MIN_DURATION_MINUTES) {
					return;
				}
			} else if (props.resizeDirection === "right") {
				// Calculate what the duration would be with the new end time
				const startTime = props.calendarEntry.startTime;
				const newDurationSeconds = newDateTime - startTime;
				const newDurationMinutes = newDurationSeconds / 60;
				
				// Only block if it would make the entry too short
				if (newDurationMinutes < MIN_DURATION_MINUTES) {
					return;
				}
			}
			
			// If we get here, the resize is valid
			resizePixelRef.current = nextResizePixel;
		}

		if (props.resizeDirection === "right") {
			const newWidth = initialWidthRef.current + deltaX;
			props.setResizeWidth(newWidth - props.calendarEntryWidth);
		}

		if (props.resizeDirection === "left") {
			const newLeft = initialLeftRef.current + deltaX;
			const newWidth = initialWidthRef.current - deltaX;
			props.setResizeLeft(newLeft - props.calendarEntryLeft);
			props.setResizeWidth(newWidth - props.calendarEntryWidth);
		}
	}

	function onResizeEnd(event: MouseEvent | TouchEvent) {
		if (isClickingRef.current) {
			props.selectScheduleEntry();
		} else if (resizePixelRef.current) {
			const newDateTime = resizePixelRef.current.dateTime.unix();
			
			// Final validation before saving
			if (props.resizeDirection === "left") {
				// Check that new start time is less than end time and maintains minimum duration
				const endTime = props.calendarEntry.endTime;
				const minStartTime = endTime - (MIN_DURATION_MINUTES * 60);
				
				if (newDateTime <= minStartTime) {
					props.onChange(newDateTime);
				} else {
					// If validation fails, set to minimum valid start time
					props.onChange(minStartTime);
				}
			} else {
				// Right resize: Check that new end time is greater than start time and maintains minimum duration
				const startTime = props.calendarEntry.startTime;
				const minEndTime = startTime + (MIN_DURATION_MINUTES * 60);
				
				if (newDateTime >= minEndTime) {
					props.onChange(newDateTime);
				} else {
					// If validation fails, set to minimum valid end time
					props.onChange(minEndTime);
				}
			}
		}

		event.stopPropagation();
		props.setResizeLeft(0);
		props.setResizeWidth(0);
		props.setIsResizing(false);
		resizePixelRef.current = undefined;
		document.removeEventListener("mouseup", onResizeEnd);
		document.removeEventListener("mousemove", onResizeMouseMove);
		if (isIpadOsRef.current) {
			document.removeEventListener("touchend", onResizeEnd);
			document.removeEventListener("touchmove", onResizeTouchMove);
		}
	}

	return (
		<Box
			onClick={(event) => event.stopPropagation()}
			onMouseDown={onResizeStart}
			onTouchStart={onResizeStart}
			className={classes.resize}
			width={props.isResizing ? props.columnWidth * 2 : 8}
			top={props.isResizing ? -30 : 0}
			bottom={props.isResizing ? -30 : 0}
			left={
				props.resizeDirection === "left"
					? props.isResizing
						? -props.columnWidth
						: 0
					: undefined
			}
			right={
				props.resizeDirection === "right"
					? props.isResizing
						? -props.columnWidth
						: 0
					: undefined
			}
		>
			
			{props.isTooltipVisible && props.isResizing && resizePixelRef.current && (
				<Box className={classes.tooltip}>
					{resizePixelRef.current.dateTime.format(t("Common_Format_Time"))}
				</Box>
			)}
		</Box>
	);
};

export default ResizeView;

const useStyles = makeStyles({
	resize: {
		// backgroundColor: "rgba(255, 0, 0, .3)",
		position: "absolute",
		cursor: "ew-resize",
		display: "flex",
		flexDirection: "column",
		justifyContent: "start",
		alignItems: "center",
		touchAction: isIpadOs() ? "none" : "unset",
		userSelect: "none",
		touchCallout: "none",
	},
	tooltip: {
		zIndex: 4,
		backgroundColor: getColorRgbaFromHex(color.text, 0.8),
		padding: `${px.xs} ${px.s}`,
		fontSize: "12px",
		color: color.totalBackground,
		borderRadius: px.xs,
	},
});
